import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { generateFaceID } from "../utils/faceUtils";
import { synthesizeSpeech } from "../utils/voiceUtils";
import { startAvatarSession } from "../utils/avatarUtils";
import TermsAndConditionsModal from "../components/TermsAndConditionsModal";

function StudioPage() {
  const [firstName, setFirstName] = useState(
    localStorage.getItem("firstName") || ""
  );
  const [emailAddress, setEmailAddress] = useState("");
  const [audioFile, setAudioFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [voiceId, setVoiceId] = useState(
    localStorage.getItem("voiceId") || "8bc1c0f2-3013-4b96-8acc-8182fa71fa86"
  );
  const [faceId, setFaceId] = useState(localStorage.getItem("faceId") || "");
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState("");
  const [audioURL, setAudioURL] = useState(null);
  const [selfiePreviewUrl, setSelfiePreviewUrl] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(
    "/images/default-avatar.jpg"
  );
  const [testSynthesisText, setTestSynthesisText] = useState(
    "Hello, this is a test of my voice."
  );
  const [synthesizedAudioUrl, setSynthesizedAudioUrl] = useState(null);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [error, setError] = useState(null);
  const [roomUrl, setRoomUrl] = useState(null);
  const [language, setLanguage] = useState("en");
  const [cloneMode, setCloneMode] = useState("stability");
  const [enhanceAudio, setEnhanceAudio] = useState(true);
  const [agentId, setAgentId] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [statusPollingInterval, setStatusPollingInterval] = useState(null);
  const [manualFaceId, setManualFaceId] = useState("");
  const [manualVoiceId, setManualVoiceId] = useState("");
  const [faceIdStatus, setFaceIdStatus] = useState("");
  const [enableManualInput, setEnableManualInput] = useState(false);

  // Terms and conditions state
  const [showTerms, setShowTerms] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(
    localStorage.getItem("termsAccepted") === "true"
  );

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const sampleText =
    "I dream is ride rolling a storytelling rollercoaster where you can lean back and enjoy the ride without having to steer.";

  // List of languages for voice cloning
  const languages = [
    { code: "en", name: "English" },
    { code: "fr", name: "French" },
    { code: "de", name: "German" },
    { code: "es", name: "Spanish" },
    { code: "pt", name: "Portuguese" },
    { code: "zh", name: "Chinese" },
    { code: "ja", name: "Japanese" },
    { code: "hi", name: "Hindi" },
    { code: "it", name: "Italian" },
    { code: "ko", name: "Korean" },
    { code: "nl", name: "Dutch" },
    { code: "pl", name: "Polish" },
    { code: "ru", name: "Russian" },
    { code: "sv", name: "Swedish" },
    { code: "tr", name: "Turkish" },
  ];

  // Add useEffect for persisting IDs
  useEffect(() => {
    if (voiceId) {
      localStorage.setItem("voiceId", voiceId);
    }
  }, [voiceId]);

  useEffect(() => {
    if (faceId) {
      localStorage.setItem("faceId", faceId);
    }
  }, [faceId]);

  useEffect(() => {
    if (firstName) {
      localStorage.setItem("firstName", firstName);
    }
  }, [firstName]);

  // Add useEffect to check face ID status on load if it exists
  useEffect(() => {
    const loadedFaceId = localStorage.getItem("faceId");
    if (loadedFaceId) {
      checkFaceIDStatus(loadedFaceId)
        .then((data) => {
          setFaceIdStatus(data.status);
          if (data.status === "pending" || data.status === "processing") {
            startStatusPolling(loadedFaceId);
          }
        })
        .catch((err) => {
          console.error("Error checking face ID status on load:", err);
        });
    }
  }, []);

  // Clear error messages after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  // Clean up status polling when component unmounts
  useEffect(() => {
    return () => {
      if (statusPollingInterval) {
        clearInterval(statusPollingInterval);
      }
    };
  }, [statusPollingInterval]);

  // Handle audio file upload
  const handleAudioFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAudioFile(file);
      setAudioURL(URL.createObjectURL(file));
    }
  };

  // Handle selfie file upload
  const handleSelfieFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelfieFile(file);
      setSelfiePreviewUrl(URL.createObjectURL(file));
    }
  };

  // Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      mediaRecorderRef.current = new MediaRecorder(stream);

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/wav" });
        const url = URL.createObjectURL(blob);
        setAudioURL(url);
        setAudioFile(blob);

        // Stop all tracks in the stream
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      setError("Microphone access is required for voice recording");
    }
  };

  // Stop voice recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Start selfie camera
  const startSelfieCamera = async () => {
    try {
      setError(null);

      // Check if camera is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError("Camera is not available on your device");
        return;
      }

      // Request camera access with appropriate constraints based on device type
      const constraints = {
        video: isMobileDevice()
          ? {
              facingMode: { exact: "user" }, // Force front camera on mobile
              width: { ideal: 1280 },
              height: { ideal: 720 },
            }
          : {
              facingMode: "user",
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
      };

      // Get camera stream
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Create video element
      const videoElement = document.createElement("video");
      videoElement.srcObject = stream;
      videoElement.autoplay = true;

      // Create a container for camera
      const cameraContainer = document.createElement("div");
      cameraContainer.id = "selfie-camera-container";
      cameraContainer.style.position = "fixed";
      cameraContainer.style.top = "0";
      cameraContainer.style.left = "0";
      cameraContainer.style.width = "100%";
      cameraContainer.style.height = "100%";
      cameraContainer.style.backgroundColor = "rgba(0,0,0,0.8)";
      cameraContainer.style.zIndex = "1000";
      cameraContainer.style.display = "flex";
      cameraContainer.style.flexDirection = "column";
      cameraContainer.style.alignItems = "center";
      cameraContainer.style.justifyContent = "center";

      // Add video to container
      const videoContainer = document.createElement("div");
      videoContainer.style.position = "relative";
      videoContainer.style.width = "80%";
      videoContainer.style.maxWidth = "640px";
      videoContainer.style.borderRadius = "8px";
      videoContainer.style.overflow = "hidden";
      videoContainer.style.boxShadow = "0 4px 20px rgba(0,0,0,0.5)";

      videoElement.style.width = "100%";
      videoElement.style.height = "auto";
      videoElement.style.display = "block";

      videoContainer.appendChild(videoElement);

      // Add capture and close buttons
      const buttonContainer = document.createElement("div");
      buttonContainer.style.display = "flex";
      buttonContainer.style.justifyContent = "center";
      buttonContainer.style.margin = "20px 0";
      buttonContainer.style.gap = "20px";

      const captureButton = document.createElement("button");
      captureButton.textContent = "Capture Selfie";
      captureButton.style.padding = "12px 24px";
      captureButton.style.backgroundColor = "#4f46e5";
      captureButton.style.color = "white";
      captureButton.style.border = "none";
      captureButton.style.borderRadius = "6px";
      captureButton.style.fontSize = "16px";
      captureButton.style.cursor = "pointer";
      captureButton.style.fontWeight = "bold";

      const closeButton = document.createElement("button");
      closeButton.textContent = "Cancel";
      closeButton.style.padding = "12px 24px";
      closeButton.style.backgroundColor = "#ef4444";
      closeButton.style.color = "white";
      closeButton.style.border = "none";
      closeButton.style.borderRadius = "6px";
      closeButton.style.fontSize = "16px";
      closeButton.style.cursor = "pointer";
      closeButton.style.fontWeight = "bold";

      buttonContainer.appendChild(captureButton);
      buttonContainer.appendChild(closeButton);

      // Add instructions
      const instructions = document.createElement("p");
      instructions.textContent =
        "Center your face in the frame and look straight at the camera";
      instructions.style.color = "white";
      instructions.style.marginBottom = "20px";
      instructions.style.textAlign = "center";
      instructions.style.fontSize = "16px";

      // Add all elements to camera container
      cameraContainer.appendChild(instructions);
      cameraContainer.appendChild(videoContainer);
      cameraContainer.appendChild(buttonContainer);

      // Add container to document
      document.body.appendChild(cameraContainer);

      // Handle closing the camera
      const closeSelfieCamera = () => {
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
        if (document.getElementById("selfie-camera-container")) {
          document.body.removeChild(cameraContainer);
        }
      };

      // Handle capture button click
      captureButton.addEventListener("click", () => {
        // Create a canvas to capture the image
        const canvas = document.createElement("canvas");
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;

        // Draw the video frame to the canvas
        const ctx = canvas.getContext("2d");
        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        // Convert canvas to blob
        canvas.toBlob(
          async (blob) => {
            // Create a file from the blob
            const selfieFile = new File([blob], "selfie.jpg", {
              type: "image/jpeg",
            });

            // Close camera
            closeSelfieCamera();

            // Create a preview of the captured image
            const reader = new FileReader();
            reader.onloadend = () => {
              // Update UI with the captured image
              setSelfieFile(selfieFile);
              setSelfiePreviewUrl(reader.result);
            };
            reader.readAsDataURL(selfieFile);
          },
          "image/jpeg",
          0.9
        );
      });

      // Handle close button click
      closeButton.addEventListener("click", closeSelfieCamera);
    } catch (err) {
      console.error("Camera error:", err);

      // Provide more helpful error messages based on the error
      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        setError(
          "Camera access denied. Please allow camera access in your browser settings and try again."
        );
      } else if (
        err.name === "NotFoundError" ||
        err.name === "DevicesNotFoundError"
      ) {
        setError(
          "No camera found on your device. Please connect a camera and try again."
        );
      } else if (
        err.name === "NotReadableError" ||
        err.name === "TrackStartError"
      ) {
        setError(
          "Camera is in use by another application. Please close other apps and try again."
        );
      } else {
        setError(`Camera error: ${err.message}`);
      }
    }
  };

  // Handle audio time update
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Handle audio loaded metadata
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  // Format time as MM:SS
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Check face ID generation status
  const checkFaceIDStatus = async (id) => {
    try {
      const response = await fetch(
        `https://api.simli.ai/getRequestStatus?face_id=${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "api-key": "n8sesdejo1tnudor5ljcg",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Status check error: ${response.status}`);
      }

      const data = await response.json();
      setFaceIdStatus(data.status);
      return data;
    } catch (err) {
      console.error("Error checking face ID status:", err);
      throw err;
    }
  };

  // Handle manual face ID input
  const handleManualFaceId = async () => {
    if (!manualFaceId) {
      setError("Please enter a face ID");
      return;
    }

    try {
      setIsProcessing(true);
      setProcessingStep("Checking face ID status...");
      setCheckingStatus(true);
      setError(null);

      const statusData = await checkFaceIDStatus(manualFaceId);

      if (
        statusData.status === "completed" ||
        statusData.status === "not_found"
      ) {
        setFaceId(manualFaceId);
        setCheckingStatus(false);
        setProcessingStep("Face ID verified successfully!");

        // Create agent if voice ID is also available
        if (voiceId) {
          try {
            const newAgentId = await createAgent(manualFaceId, voiceId);
            setAgentId(newAgentId);
            setProcessingStep("Avatar and agent created successfully!");
          } catch (err) {
            setError(`Agent creation error: ${err.message}`);
          }
        }
      } else if (statusData.status === "failed") {
        setError(
          "Face ID processing failed. Please try with a different image or ID."
        );
      } else {
        // Handle pending or processing status
        setFaceId(manualFaceId);
        startStatusPolling(manualFaceId);
      }
    } catch (err) {
      setError(`Error verifying face ID: ${err.message}`);
    } finally {
      // Only stop processing if we're not still checking status
      if (!checkingStatus) {
        setIsProcessing(false);
      }
    }
  };

  // Handle manual voice ID input
  const handleManualVoiceId = () => {
    if (!manualVoiceId) {
      setError("Please enter a voice ID");
      return;
    }

    setVoiceId(manualVoiceId);
    setProcessingStep("Voice ID set successfully!");
  };

  // Create agent with Simli API
  const createAgent = async (faceId, voiceId) => {
    try {
      setProcessingStep("Creating agent...");

      const agentData = {
        face_id: faceId,
        name: `${firstName || "User"}'s Agent`,
        first_message:
          "Hello! I'm your digital avatar. How can I assist you today?",
        prompt: "You are a helpful digital avatar assistant created by Simli.",
        voice_provider: "cartesia",
        voice_id: voiceId,
        voice_model: "stability",
        language: language,
        llm_model: "gpt-4o-mini",
        max_idle_time: 300,
        max_session_length: 3600,
      };

      const response = await fetch("https://api.simli.ai/agent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-simli-api-key": "n8sesdejo1tnudor5ljcg",
        },
        body: JSON.stringify(agentData),
      });

      if (!response.ok) {
        throw new Error(`Agent creation error: ${response.status}`);
      }

      const data = await response.json();
      return data.id;
    } catch (err) {
      console.error("Error creating agent:", err);
      throw err;
    }
  };

  // Start polling for status
  const startStatusPolling = (id) => {
    // Clear any existing polling interval
    if (statusPollingInterval) {
      clearInterval(statusPollingInterval);
    }

    setCheckingStatus(true);
    setProcessingStep(
      `Waiting for avatar generation... ⌛️ (${faceIdStatus || "pending"})`
    );

    const pollInterval = setInterval(async () => {
      try {
        const statusData = await checkFaceIDStatus(id);

        if (
          statusData.status === "completed" ||
          statusData.status === "not_found"
        ) {
          clearInterval(pollInterval);
          setStatusPollingInterval(null);
          setCheckingStatus(false);

          // Create agent once face is ready
          if (voiceId) {
            const newAgentId = await createAgent(id, voiceId);
            setAgentId(newAgentId);
            setProcessingStep("Avatar and agent created successfully!");
          } else {
            setProcessingStep(
              "Avatar generated successfully! Please provide a voice ID to create an agent."
            );
          }
          setIsProcessing(false);
        } else if (statusData.status === "failed") {
          clearInterval(pollInterval);
          setStatusPollingInterval(null);
          setCheckingStatus(false);
          setError(
            "Avatar generation failed. Please try with a different image."
          );
          setIsProcessing(false);
        } else {
          // Still processing or pending, continue polling
          setProcessingStep(
            `Waiting for avatar generation... ⌛️ (${statusData.status})`
          );
        }
      } catch (err) {
        clearInterval(pollInterval);
        setStatusPollingInterval(null);
        setCheckingStatus(false);
        setError(`Status check error: ${err.message}`);
        console.error("Status check error:", err);
        setIsProcessing(false);
      }
    }, 4000);

    setStatusPollingInterval(pollInterval);
  };

  // Test synthesized speech with cloned voice
  const testVoiceSynthesis = async () => {
    if (!voiceId) {
      setError("Please clone your voice first");
      return;
    }

    if (!testSynthesisText.trim()) {
      setError("Please enter text to synthesize");
      return;
    }

    try {
      setIsSynthesizing(true);
      setError(null);

      const url = await synthesizeSpeech(testSynthesisText, voiceId);
      setSynthesizedAudioUrl(url);

      // Play the audio
      const audio = new Audio(url);
      audio.play();
    } catch (err) {
      setError(`Speech synthesis error: ${err.message}`);
      console.error("Speech synthesis error:", err);
    } finally {
      setIsSynthesizing(false);
    }
  };

  // Clone voice function using Cartesia API
  const cloneVoice = async () => {
    if (!firstName || !audioFile) {
      setError("Please provide your first name and a voice recording");
      return;
    }

    try {
      setIsProcessing(true);
      setProcessingStep("Cloning voice with Cartesia...");
      setError(null);

      // Create form data for the multipart request
      const formData = new FormData();
      formData.append("clip", audioFile);
      formData.append("name", `${firstName}'s voice`);
      formData.append("description", `Voice clone for ${firstName}`);
      formData.append("language", language);
      formData.append("mode", cloneMode);
      formData.append("enhance", enhanceAudio.toString());

      // Make API request to Cartesia
      const response = await fetch("https://api.cartesia.ai/voices/clone", {
        method: "POST",
        body: formData,
        headers: {
          "X-API-Key": "sk_car_FUgvuidCgy7CrUWjK4xpcW", // Replace with actual API key
          "Cartesia-Version": "2024-11-13",
        },
      });

      if (!response.ok) {
        throw new Error(`Cartesia API error: ${response.status}`);
      }

      const data = await response.json();
      setVoiceId(data.id);

      setProcessingStep("Voice cloned successfully!");
    } catch (err) {
      setError(`Voice cloning error: ${err.message}`);
      console.error("Voice cloning error:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Generate avatar function
  const generateAvatar = async () => {
    if (!emailAddress || !selfieFile) {
      setError("Please provide your email address and a selfie");
      return;
    }

    try {
      setIsProcessing(true);
      setProcessingStep("Generating face ID...");
      setError(null);
      setCheckingStatus(false);

      // Clear any existing polling interval
      if (statusPollingInterval) {
        clearInterval(statusPollingInterval);
        setStatusPollingInterval(null);
      }

      // Call the Simli API to generate a face ID
      const id = await generateFaceID(selfieFile);
      setFaceId(id);

      // Start polling for status
      startStatusPolling(id);
    } catch (err) {
      setError(`Avatar generation error: ${err.message}`);
      console.error("Avatar generation error:", err);
      setCheckingStatus(false);
      setIsProcessing(false);
    }
  };

  // Start avatar session
  const startSession = async () => {
    if (!faceId || !voiceId) {
      setError("Both face ID and voice ID are required to start a session");
      return;
    }

    try {
      setIsProcessing(true);
      setProcessingStep("Starting avatar session...");
      setError(null);

      const url = await startAvatarSession(faceId, voiceId, firstName);
      setRoomUrl(url);
    } catch (err) {
      setError(`Session error: ${err.message}`);
      console.error("Session error:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Function to handle terms acceptance
  const handleAcceptTerms = () => {
    setHasAcceptedTerms(true);
    localStorage.setItem("termsAccepted", "true");
    setShowTerms(false);
  };

  // Show terms when user clicks on first name field if they haven't accepted yet
  const handleFirstNameClick = () => {
    if (!hasAcceptedTerms) {
      setShowTerms(true);
    }
  };

  // Helper function to detect mobile devices
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  };

  return (
    <div className="h-screen bg-white flex flex-col overflow-hidden">
      {/* Terms and Conditions Modal */}
      <TermsAndConditionsModal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        onAccept={handleAcceptTerms}
      />

      {/* Header */}
      <header className="py-2 px-8 bg-white flex justify-between items-center border-b border-gray-200 ">
        <div className="text-[#23c55e] text-3xl font-bold">
          <Link to="/">YOO!</Link>
        </div>

        <div className="space-x-8 md:flex md:items-center">
          <Link to="/yoo-create">Yoo Create</Link>
          <Link to="/yoo-chat">Yoo Chat</Link>
          <Link to="/joke">Yoo Funny</Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow flex overflow-hidden">
        {/* Left Column - Voice Cloning */}
        <div className="w-1/4 bg-gray-50 p-4 overflow-y-auto flex flex-col justify-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">
              Step 1: Clone Voice with Cartesia
            </h2>

            {voiceId && (
              <div className="mb-4">
                <div className="text-blue-500 font-medium">
                  Voice ID: {voiceId}
                </div>
              </div>
            )}

            <div onClick={handleFirstNameClick} className="mb-6">
              <label htmlFor="firstName" className="block text-gray-700 mb-2">
                Your first name
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
                disabled={!hasAcceptedTerms}
              />
              {!hasAcceptedTerms && (
                <p className="text-xs text-gray-500 mt-1">
                  Click to view and accept Terms & Conditions before proceeding
                </p>
              )}
            </div>

            {/* <div className="mb-6">
              <label htmlFor="language" className="block text-gray-700 mb-2">
                Language
              </label>
              <select
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div> */}

            {/* <div className="mb-6">
              <label htmlFor="cloneMode" className="block text-gray-700 mb-2">
                Cloning Mode
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="cloneMode"
                    value="stability"
                    checked={cloneMode === "stability"}
                    onChange={() => setCloneMode("stability")}
                    className="mr-2"
                  />
                  <span className="text-sm">Stability (10-20s, more stable)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="cloneMode"
                    value="similarity"
                    checked={cloneMode === "similarity"}
                    onChange={() => setCloneMode("similarity")}
                    className="mr-2"
                  />
                  <span className="text-sm">Similarity (5s, more authentic)</span>
                </label>
              </div>
            </div> */}

            {/* <div className="mb-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={enhanceAudio}
                  onChange={(e) => setEnhanceAudio(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">
                  Enhance audio (reduce background noise)
                </span>
              </label>
            </div> */}

            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                Upload an audio clip
                {cloneMode === "stability"
                  ? " (10-20 seconds recommended)"
                  : " (5 seconds recommended)"}
              </p>
              <input
                type="file"
                accept="audio/*"
                onChange={handleAudioFileChange}
                className="w-full border border-gray-300 rounded p-2"
                disabled={!hasAcceptedTerms}
              />
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-2">Or record voice clip</p>
              {isRecording ? (
                <button
                  onClick={stopRecording}
                  disabled={!hasAcceptedTerms}
                  className={`w-full bg-[#23c55e] text-white py-3 rounded ${
                    !hasAcceptedTerms ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Stop
                </button>
              ) : (
                <button
                  onClick={startRecording}
                  disabled={!hasAcceptedTerms}
                  className={`w-full bg-gray-200 text-gray-800 py-3 rounded hover:bg-gray-300 ${
                    !hasAcceptedTerms ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Record
                </button>
              )}
              {!hasAcceptedTerms && (
                <p className="text-xs text-gray-500 mt-1">
                  Accept Terms & Conditions to begin recording
                </p>
              )}
            </div>

            {audioURL && (
              <div className="mb-6">
                <audio
                  ref={audioRef}
                  src={audioURL}
                  controls={false}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  className="hidden"
                />
                <div className="flex items-center">
                  <button
                    onClick={() => audioRef.current.play()}
                    className="mr-2 text-gray-700"
                  >
                    ▶
                  </button>
                  <div className="flex-grow bg-gray-200 h-1 rounded-full overflow-hidden">
                    <div
                      className="bg-red-500 h-1"
                      style={{ width: `${(currentTime / duration) * 100}%` }}
                    ></div>
                  </div>
                  <span className="ml-2 text-xs text-gray-600">
                    {formatTime(currentTime)}
                  </span>
                </div>
              </div>
            )}

            <div className="mb-10">
              <h3 className="text-indigo-700 mb-2">Sample text to read</h3>
              <p className="text-gray-600 text-sm">{sampleText}</p>
            </div>

            <button
              onClick={cloneVoice}
              disabled={isProcessing || !audioFile || !hasAcceptedTerms}
              className={`w-full bg-[#23c55e] text-white py-3 rounded font-medium ${
                isProcessing || !audioFile || !hasAcceptedTerms
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              {isProcessing && processingStep.includes("voice")
                ? "Processing..."
                : "Clone Voice"}
            </button>

            {/* Toggle button for manual input */}
            {/* Manual Voice ID Input */}
            {false && (
              <div className="mt-6 p-4 border border-gray-300 rounded bg-gray-100">
                <h3 className="text-md font-medium text-gray-800 mb-3">
                  Or Enter Voice ID Manually
                </h3>
                <div className="flex">
                  <input
                    type="text"
                    value={manualVoiceId}
                    onChange={(e) => setManualVoiceId(e.target.value)}
                    placeholder="Enter voice ID"
                    className="flex-grow p-2 border border-gray-300 rounded-l"
                  />
                  <button
                    onClick={handleManualVoiceId}
                    className="bg-blue-500 text-white py-2 px-4 rounded-r"
                  >
                    Set
                  </button>
                </div>
              </div>
            )}

            {/* Test Voice Synthesis */}
            {/* {voiceId && (
              <div className="mt-6">
                <h3 className="text-indigo-700 mb-2">Test Your Voice</h3>
                <textarea
                  value={testSynthesisText}
                  onChange={(e) => setTestSynthesisText(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded mb-2"
                  rows="2"
                />
                <button
                  onClick={testVoiceSynthesis}
                  disabled={isSynthesizing}
                  className={`w-full bg-blue-500 text-white py-2 rounded ${
                    isSynthesizing ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isSynthesizing ? "Synthesizing..." : "Test Voice"}
                </button>

                {synthesizedAudioUrl && (
                  <div className="mt-2">
                    <audio
                      src={synthesizedAudioUrl}
                      controls
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            )} */}
          </div>
        </div>

        {/* Center Column - Avatar Preview */}
        <div className="w-2/4 bg-blue-500 flex flex-col items-center justify-center p-4 overflow-y-auto">
          {isProcessing && (
            <div className="text-white text-center bg-black bg-opacity-50 p-4 rounded mb-4 z-10">
              <div
                className={`${
                  checkingStatus
                    ? "text-4xl mb-2"
                    : "animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-white mx-auto mb-2"
                }`}
              >
                {checkingStatus ? "⌛️" : ""}
              </div>
              <p>{processingStep}</p>
              {faceIdStatus && checkingStatus && (
                <p className="mt-2 text-sm">
                  Status:{" "}
                  <span className="font-bold capitalize">{faceIdStatus}</span>
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="text-white text-center bg-red-500 bg-opacity-70 p-4 rounded mb-4 z-10">
              {error}
            </div>
          )}

          {!roomUrl ? (
            <div className="flex flex-col items-center">
              {selfiePreviewUrl && (
                <img
                  src={selfiePreviewUrl}
                  alt="Avatar Preview"
                  className="max-w-[80%] max-h-[70vh] object-contain rounded-md shadow-lg"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/placeholder-avatar.jpeg";
                  }}
                />
              )}

              {faceId && voiceId && !checkingStatus && (
                <button
                  onClick={startSession}
                  className="mt-6 bg-white text-blue-500 px-6 py-3 rounded-full font-semibold shadow-lg hover:bg-gray-100"
                >
                  Start Avatar Session
                </button>
              )}
            </div>
          ) : (
            <iframe
              src={roomUrl}
              title="Simli Chat Session"
              className="w-[95%] h-[90vh] rounded-md shadow-lg"
              allow="camera; microphone; fullscreen"
            />
          )}
        </div>

        {/* Right Column - Avatar Generation */}
        <div className="w-1/4 bg-gray-50 p-4 overflow-y-auto flex flex-col justify-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">
              Step 2: Generate Avatar
            </h2>

            {faceId && (
              <div className="mb-6">
                <div className="text-blue-500 font-medium">
                  Face ID: {faceId}
                </div>
                {faceIdStatus && (
                  <div className="text-sm mt-1">
                    Status:{" "}
                    <span className="capitalize font-medium text-gray-700">
                      {faceIdStatus}
                    </span>
                  </div>
                )}
              </div>
            )}

            {agentId && (
              <div className="mb-6">
                <div className="text-green-600 font-medium">
                  Agent ID: {agentId}
                </div>
              </div>
            )}

            <div className="mb-6">
              <label
                htmlFor="emailAddress"
                className="block text-gray-700 mb-2"
              >
                Your email address
              </label>
              <input
                type="email"
                id="emailAddress"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
                disabled={!hasAcceptedTerms}
              />
              {!hasAcceptedTerms && (
                <p className="text-xs text-gray-500 mt-1">
                  Accept Terms & Conditions to proceed
                </p>
              )}
            </div>

            <div className="mb-4">
              <p className="text-gray-700 mb-2">Upload a selfie</p>
              <input
                type="file"
                accept="image/*"
                onChange={handleSelfieFileChange}
                className="w-full border border-gray-300 rounded p-2"
                disabled={!hasAcceptedTerms}
              />
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-2">Or record a selfie</p>
              <button
                onClick={startSelfieCamera}
                disabled={!hasAcceptedTerms}
                className={`w-full bg-blue-500 text-white py-3 rounded hover:bg-blue-600 ${
                  !hasAcceptedTerms ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Record
              </button>
            </div>

            {selfiePreviewUrl && (
              <div className="mb-10 flex justify-center">
                <img
                  src={selfiePreviewUrl}
                  alt="Selfie Preview"
                  className="w-40 h-40 object-cover border-2 border-gray-300 rounded"
                />
              </div>
            )}

            <button
              onClick={generateAvatar}
              disabled={isProcessing || !selfieFile || !hasAcceptedTerms}
              className={`w-full bg-[#23c55e] text-white py-3 rounded font-medium ${
                isProcessing || !selfieFile || !hasAcceptedTerms
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              {isProcessing && processingStep.includes("avatar")
                ? "Processing..."
                : "Generate Avatar"}
            </button>

            {/* Toggle button for manual input */}

            {/* Manual Face ID Input */}
            {false && (
              <div className="mt-6 p-4 border border-gray-300 rounded bg-gray-100">
                <h3 className="text-md font-medium text-gray-800 mb-3">
                  Or Enter Face ID Manually
                </h3>
                <div className="flex">
                  <input
                    type="text"
                    value={manualFaceId}
                    onChange={(e) => setManualFaceId(e.target.value)}
                    placeholder="Enter face ID"
                    className="flex-grow p-2 border border-gray-300 rounded-l"
                    disabled={!hasAcceptedTerms}
                  />
                  <button
                    onClick={handleManualFaceId}
                    disabled={isProcessing || !hasAcceptedTerms}
                    className={`bg-blue-500 text-white py-2 px-4 rounded-r ${
                      isProcessing || !hasAcceptedTerms
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Verify
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  We'll verify the status of the Face ID before using it.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white py-4 border-t border-gray-200 text-center text-gray-600 text-sm">
        <p>© 2023 YOO! All rights reserved.</p>
      </footer>
    </div>
  );
}

export default StudioPage;
